import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SalvButton, SalvIconButton, SalvTextArea, Size } from '@salvapp/ui-components';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { InternalApiCaseStatus, InternalApiNoteDraft } from '../../api-internal';
import { useAutoSaveNoteDraft } from '../../note-drafts/useAutoSaveNoteDraft';
import { AutoSaveIndicator } from '../../alerts/management/notes/AutoSaveIndicator';
import {
  useCreateCaseNoteDraft,
  useUpdateCaseNoteDraft,
  CASE_NOTE_DRAFTS_QUERY_KEY,
} from './caseNoteDraftQueryActions';
import CaseStatusDropdown from '../management/CaseStatusDropdown';
import { useQueryClient } from '@tanstack/react-query';

interface CaseNoteEditorProps {
  handle: string;
  existingDraft?: InternalApiNoteDraft | null;
  assignedStatusCode: string;
  pendingStatus?: InternalApiCaseStatus;
  isDraftEnabled: boolean;
  onSubmit: (content: string, status?: InternalApiCaseStatus, draftId?: number) => Promise<void>;
  onCancel: () => void;
  onDraftIdChange?: (draftId: number | null) => void;
  onPendingStatusChange: (status: InternalApiCaseStatus | undefined) => void;
}

export const CaseNoteEditor = ({
  handle,
  existingDraft,
  assignedStatusCode,
  pendingStatus,
  isDraftEnabled,
  onSubmit,
  onCancel,
  onDraftIdChange,
  onPendingStatusChange,
}: CaseNoteEditorProps) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const createDraftMutation = useCreateCaseNoteDraft(handle);
  const updateDraftMutation = useUpdateCaseNoteDraft(handle);

  const { content, setContent, saveState, currentDraftId, save, cancel, discard, resume } = useAutoSaveNoteDraft({
    mutations: {
      create: (content) => createDraftMutation.mutateAsync(content),
      update: (id, content) => updateDraftMutation.mutateAsync({ id, content }),
    },
    existingDraft,
    enabled: isDraftEnabled,
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    onDraftIdChange?.(currentDraftId);
  }, [currentDraftId, onDraftIdChange]);

  const handleSubmit = useCallback(async () => {
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      const finalDraftId = await discard();
      await onSubmit(content, pendingStatus, finalDraftId ?? undefined);

      if (finalDraftId && isDraftEnabled) {
        await queryClient.invalidateQueries({
          queryKey: [CASE_NOTE_DRAFTS_QUERY_KEY, handle],
        });
      }
    } catch {
      resume();
    } finally {
      setIsSubmitting(false);
    }
  }, [content, pendingStatus, isDraftEnabled, handle, queryClient, onSubmit, discard, resume]);

  const handleCancel = useCallback(async () => {
    if (isDraftEnabled) {
      await cancel();
      await queryClient.invalidateQueries({
        queryKey: [CASE_NOTE_DRAFTS_QUERY_KEY, handle],
      });
    }
    onCancel();
  }, [isDraftEnabled, cancel, queryClient, handle, onCancel]);

  return (
    <div className='p-a-16 new-note'>
      <SalvTextArea
        ref={inputRef}
        ariaLabel='Note content'
        placeholder='Enter your note here...'
        value={content}
        onChange={setContent}
        size={Size.EXTRA_SMALL}
        autoSizing={true}
        className='m-b-16'
      />
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <CaseStatusDropdown
            label={pendingStatus?.textName ?? 'Add status'}
            icon={pendingStatus ? undefined : faPlus}
            size={Size.SMALL}
            assignedStatusCode={assignedStatusCode}
            onStatusSelect={(status) => onPendingStatusChange(status)}
          />
          {pendingStatus && (
            <SalvIconButton
              icon={faXmark}
              onClick={() => onPendingStatusChange(undefined)}
              ariaLabel='Remove pending status'
            />
          )}
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {isDraftEnabled && (
            <AutoSaveIndicator
              status={saveState.status}
              lastSavedAt={saveState.lastSavedAt}
              errorMessage={saveState.errorMessage}
              onRetry={save}
            />
          )}
          <SalvButton
            label={isDraftEnabled ? 'Close' : 'Cancel'}
            variant='muted'
            size={Size.SMALL}
            onClick={handleCancel}
            disabled={isSubmitting}
          />
          <SalvButton
            label='Submit'
            size={Size.SMALL}
            disabled={!content.trim() || isSubmitting}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </div>
  );
};
